/**
 * Operations de lecture/ecriture sur la base locale.
 *
 * L'UI ne touche jamais Dexie directement : elle passe par ces fonctions, qui
 * garantissent les invariants du domaine (horodatages, resolution figee des
 * repas, unicite des episodes en cours).
 */

import { dayKeyOf, nowInstant } from '../domain/time'
import type {
  DailyLog,
  DayKey,
  Id,
  Ingredient,
  Meal,
  MealSource,
  Recipe,
  RecipeItem,
  Sensation,
  Stool,
} from '../domain/types'
import { db, nouvelId } from './db'

// ---------------------------------------------------------------------------
// Ingredients
// ---------------------------------------------------------------------------

export async function listerIngredients(): Promise<Ingredient[]> {
  const tout = await db.ingredients.toArray()
  return tout
    .filter((i) => !i.archive)
    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
}

export async function listerTousIngredients(): Promise<Ingredient[]> {
  const tout = await db.ingredients.toArray()
  return tout.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
}

export async function creerIngredient(
  data: Omit<Ingredient, 'id' | 'creeLe' | 'modifieLe' | 'perso'>,
): Promise<Ingredient> {
  const t = nowInstant()
  const ing: Ingredient = { ...data, id: nouvelId(), perso: true, creeLe: t, modifieLe: t }
  await db.ingredients.add(ing)
  return ing
}

export async function majIngredient(id: Id, patch: Partial<Ingredient>): Promise<void> {
  await db.ingredients.update(id, { ...patch, modifieLe: nowInstant() })
}

/**
 * Supprime un ingredient, ou l'archive s'il est deja utilise.
 *
 * Un repas fige sa composition : supprimer l'ingredient laisserait des
 * references orphelines dans l'historique et amputerait l'analyse. L'archivage
 * le retire des listes de saisie sans toucher au passe.
 */
export async function supprimerIngredient(id: Id): Promise<'supprime' | 'archive'> {
  const utiliseRepas = await db.meals
    .filter((m) => m.ingredientsResolus.some((r) => r.ingredientId === id))
    .count()
  const utiliseRecette = await db.recipes
    .filter((r) => r.items.some((i) => i.ingredientId === id))
    .count()

  if (utiliseRepas > 0 || utiliseRecette > 0) {
    await majIngredient(id, { archive: true })
    return 'archive'
  }
  await db.ingredients.delete(id)
  return 'supprime'
}

/** Recherche insensible a la casse et aux accents, sur le nom et les synonymes. */
export function normaliser(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

/**
 * Pertinence d'un libelle vis-a-vis d'une requete : plus bas = plus pertinent.
 *
 * Un simple `includes` ne suffit plus depuis que le referentiel compte plusieurs
 * centaines d'entrees : chercher « pates » remonte aussi « pate feuilletee »,
 * « papaye » ou « parmesan », et l'aliment cherche se retrouve noye — voire
 * hors de la liste si celle-ci est tronquee.
 */
function pertinence(nom: string, synonymes: readonly string[], q: string): number {
  const n = normaliser(nom)
  if (n === q) return 0
  if (n.startsWith(q)) return 1
  // Debut d'un mot interne : « ble » doit remonter « farine de ble ».
  if (n.split(/[\s()/,'-]+/).some((mot) => mot.startsWith(q))) return 2
  if (n.includes(q)) return 3
  const s = synonymes.map(normaliser)
  if (s.some((x) => x.startsWith(q))) return 4
  if (s.some((x) => x.includes(q))) return 5
  return Number.POSITIVE_INFINITY
}

/** Filtre et **classe par pertinence** ; a pertinence egale, ordre alphabetique. */
export function filtrerIngredients(liste: Ingredient[], requete: string): Ingredient[] {
  const q = normaliser(requete)
  if (!q) return liste
  return liste
    .map((i) => ({ i, p: pertinence(i.nom, i.synonymes, q) }))
    .filter((x) => Number.isFinite(x.p))
    .sort((a, b) => a.p - b.p || a.i.nom.localeCompare(b.i.nom, 'fr'))
    .map((x) => x.i)
}

/** Meme classement, pour les recettes (qui n'ont pas de synonymes). */
export function filtrerRecettes(liste: Recipe[], requete: string): Recipe[] {
  const q = normaliser(requete)
  if (!q) return liste
  return liste
    .map((r) => ({ r, p: pertinence(r.nom, [], q) }))
    .filter((x) => Number.isFinite(x.p))
    .sort((a, b) => a.p - b.p || a.r.nom.localeCompare(b.r.nom, 'fr'))
    .map((x) => x.r)
}

// ---------------------------------------------------------------------------
// Recettes
// ---------------------------------------------------------------------------

export async function listerRecettes(): Promise<Recipe[]> {
  const tout = await db.recipes.toArray()
  return tout
    .filter((r) => !r.archivee)
    .sort((a, b) => {
      if (a.favorite !== b.favorite) return a.favorite ? -1 : 1
      return a.nom.localeCompare(b.nom, 'fr')
    })
}

export async function lireRecette(id: Id): Promise<Recipe | undefined> {
  return db.recipes.get(id)
}

export async function creerRecette(
  data: Omit<Recipe, 'id' | 'creeLe' | 'modifieLe'>,
): Promise<Recipe> {
  const t = nowInstant()
  const r: Recipe = { ...data, id: nouvelId(), creeLe: t, modifieLe: t }
  await db.recipes.add(r)
  return r
}

export async function majRecette(id: Id, patch: Partial<Recipe>): Promise<void> {
  await db.recipes.update(id, { ...patch, modifieLe: nowInstant() })
}

export async function supprimerRecette(id: Id): Promise<'supprime' | 'archive'> {
  const utilisee = await db.meals
    .filter((m) => m.sources.some((s) => s.kind === 'recipe' && s.recipeId === id))
    .count()
  if (utilisee > 0) {
    await majRecette(id, { archivee: true })
    return 'archive'
  }
  await db.recipes.delete(id)
  return 'supprime'
}

// ---------------------------------------------------------------------------
// Repas
// ---------------------------------------------------------------------------

/**
 * Developpe les sources d'un repas en une liste plate d'ingredients.
 *
 * Le resultat est **fige** dans le repas (`ingredientsResolus`) : c'est
 * l'invariant central du modele. Voir le commentaire de `Meal` dans
 * `domain/types.ts`.
 */
export function resoudreComposition(
  sources: readonly MealSource[],
  recettes: readonly Recipe[],
): RecipeItem[] {
  const parId = new Map(recettes.map((r) => [r.id, r]))
  const cumul = new Map<string, RecipeItem>()

  const ajouter = (ingredientId: Id, qte: number, unite: RecipeItem['unite']) => {
    const cle = `${ingredientId}|${unite}`
    const existant = cumul.get(cle)
    if (existant) existant.qte += qte
    else cumul.set(cle, { ingredientId, qte, unite })
  }

  for (const s of sources) {
    if (s.kind === 'ingredient') {
      ajouter(s.ingredientId, s.qte, s.unite)
      continue
    }
    const r = parId.get(s.recipeId)
    if (!r || r.portions <= 0) continue
    const facteur = s.portions / r.portions
    for (const item of r.items) ajouter(item.ingredientId, item.qte * facteur, item.unite)
  }

  return [...cumul.values()]
}

export async function listerRepas(limite = 200): Promise<Meal[]> {
  return db.meals.orderBy('at').reverse().limit(limite).toArray()
}

export async function listerTousRepas(): Promise<Meal[]> {
  return db.meals.orderBy('at').toArray()
}

export async function lireRepas(id: Id): Promise<Meal | undefined> {
  return db.meals.get(id)
}

export async function creerRepas(
  data: Omit<Meal, 'id' | 'creeLe' | 'modifieLe' | 'ingredientsResolus'>,
): Promise<Meal> {
  const recettes = await db.recipes.toArray()
  const t = nowInstant()
  const m: Meal = {
    ...data,
    id: nouvelId(),
    ingredientsResolus: resoudreComposition(data.sources, recettes),
    creeLe: t,
    modifieLe: t,
  }
  await db.meals.add(m)
  return m
}

export async function majRepas(
  id: Id,
  patch: Partial<Omit<Meal, 'ingredientsResolus'>>,
): Promise<void> {
  const patchFinal: Partial<Meal> = { ...patch, modifieLe: nowInstant() }
  // La composition n'est recalculee que si les sources changent : une
  // modification de la recette entre-temps ne doit pas contaminer ce repas.
  if (patch.sources) {
    const recettes = await db.recipes.toArray()
    patchFinal.ingredientsResolus = resoudreComposition(patch.sources, recettes)
  }
  await db.meals.update(id, patchFinal)
}

export async function supprimerRepas(id: Id): Promise<void> {
  await db.meals.delete(id)
}

// ---------------------------------------------------------------------------
// Sensations (evenements ponctuels)
// ---------------------------------------------------------------------------

export async function listerSensations(limite = 300): Promise<Sensation[]> {
  return db.sensations.orderBy('at').reverse().limit(limite).toArray()
}

export async function listerToutesSensations(): Promise<Sensation[]> {
  return db.sensations.orderBy('at').toArray()
}

export async function lireSensation(id: Id): Promise<Sensation | undefined> {
  return db.sensations.get(id)
}

export async function creerSensation(
  data: Omit<Sensation, 'id' | 'creeLe' | 'modifieLe'>,
): Promise<Sensation> {
  const t = nowInstant()
  const e: Sensation = { ...data, id: nouvelId(), creeLe: t, modifieLe: t }
  await db.sensations.add(e)
  return e
}

export async function majSensation(id: Id, patch: Partial<Sensation>): Promise<void> {
  await db.sensations.update(id, { ...patch, modifieLe: nowInstant() })
}

export async function supprimerSensation(id: Id): Promise<void> {
  await db.sensations.delete(id)
}

/** Sensations notees au cours des dernieres heures : sert a proposer un rappel. */
export async function sensationsRecentes(heures = 24): Promise<Sensation[]> {
  const seuil = Date.now() - heures * 3_600_000
  const tout = await db.sensations.orderBy('at').reverse().limit(60).toArray()
  return tout.filter((e) => Date.parse(e.at) >= seuil)
}

// ---------------------------------------------------------------------------
// Journal quotidien
// ---------------------------------------------------------------------------

export async function lireJour(date: DayKey): Promise<DailyLog | undefined> {
  return db.dailyLogs.get(date)
}

export async function listerJours(): Promise<DailyLog[]> {
  return db.dailyLogs.orderBy('date').toArray()
}

export async function sauverJour(log: Omit<DailyLog, 'modifieLe'>): Promise<void> {
  await db.dailyLogs.put({ ...log, modifieLe: nowInstant() })
}

// ---------------------------------------------------------------------------
// Passages aux selles
// ---------------------------------------------------------------------------

export async function listerSelles(limite = 300): Promise<Stool[]> {
  return db.stools.orderBy('at').reverse().limit(limite).toArray()
}

export async function listerToutesSelles(): Promise<Stool[]> {
  return db.stools.orderBy('at').toArray()
}

export async function lireSelle(id: Id): Promise<Stool | undefined> {
  return db.stools.get(id)
}

export async function creerSelle(
  data: Omit<Stool, 'id' | 'creeLe' | 'modifieLe'>,
): Promise<Stool> {
  const t = nowInstant()
  const s: Stool = { ...data, id: nouvelId(), creeLe: t, modifieLe: t }
  await db.stools.add(s)
  return s
}

export async function majSelle(id: Id, patch: Partial<Stool>): Promise<void> {
  await db.stools.update(id, { ...patch, modifieLe: nowInstant() })
}

export async function supprimerSelle(id: Id): Promise<void> {
  await db.stools.delete(id)
}

/**
 * Passages d'une journee donnee.
 *
 * `at` est stocke en UTC alors que la journee demandee est locale : on filtre
 * donc sur la cle de jour locale plutot que sur une plage d'instants, sans quoi
 * un passage de fin de soiree pourrait basculer au lendemain.
 */
export async function sellesDuJour(date: DayKey): Promise<Stool[]> {
  const tout = await db.stools.orderBy('at').toArray()
  return tout.filter((s) => dayKeyOf(s.at) === date)
}
